<template>
  <b-container class="my-5">
    <b-row>
      <b-col>
        <b-breadcrumb :items="breadcrumbs" />
      </b-col>
    </b-row>

    <b-row>
      <b-col
        sm="12"
        md="8"
        lg="9"
      >
        <b-alert
          variant="warning"
          :show="noRooms"
        >
          {{ $t('alerts.priest.no_conference_rooms') }}
        </b-alert>

        <b-card-group
          deck
          v-for="(rooms, idx) in rows"
          :key="`row-${idx}`"
          class="mb-3"
        >
          <b-card
            v-for="room in rooms"
            :key="room.id"
            no-body
            text-variant="white"
            class="room cursor"
            :class="{'add-room': room.id === 'create'}"
            @click="selectConferenceRoom(room)"
          >
            <template v-if="room.id === 'create'">
              <b-card-body
                body-bg-variant="default"
                class="d-flex justify-content-center align-items-center"
                style="min-height: 12rem;"
                v-b-tooltip="$t('cabinet.conference.open_room')"
              >
                <font-awesome-icon
                  icon="plus"
                  class="align-middle"
                />
              </b-card-body>
            </template>
            <template v-else>
              <b-card-img
                v-if="room.image"
                :src="`https://api.evangelie.life/${room.image}`"
              />
              <b-card-body body-bg-variant="secondary">
                <b-card-text text-tag="h5">
                  {{ room.title }}
                </b-card-text>
                <b-card-text text-tag="div">
                  <b-container
                    fluid
                    class="px-0 mt-3"
                  >
                    <b-row
                      v-for="row in Array.from(Array(Math.ceil(schedule(room.schedule).length/columns)).keys())"
                      :key="`row-${row}`"
                      class="mt-2"
                    >
                      <b-col
                        v-for="(schedule, index) in schedule(room.schedule).slice(row * columns, row * columns + columns)"
                        :key="`schedule-${index}`"
                      >
                        <b-badge
                          variant="light"
                          class="d-block"
                        >
                          {{ schedule }}
                        </b-badge>
                      </b-col>
                      <template v-if="schedule(room.schedule).slice(row * columns, row * columns + columns).length < columns">
                        <b-col>&nbsp;</b-col>
                      </template>
                    </b-row>
                  </b-container>
                </b-card-text>
              </b-card-body>
              <b-card-footer class="bg-secondary">
                {{ $t('cabinet.conference.online') }} {{ room.online }}
              </b-card-footer>

              <div
                v-if="isOwner"
                class="hover"
              >
                <b-button
                  size="sm"
                  variant="info"
                  class="mr-1"
                  @click.stop="editConferenceRoom(room)"
                  v-b-tooltip
                  :title="$t('cabinet.conference.edit_room')"
                >
                  <font-awesome-icon :icon="['far', 'edit']" />
                </b-button>
                <b-button
                  size="sm"
                  variant="danger"
                  class=""
                  @click.stop="closeConferenceRoom(room)"
                >
                  <font-awesome-icon :icon="['fas', 'trash-alt']" />
                </b-button>
              </div>
            </template>
          </b-card>

          <template v-if="(idx === (rows.length - 1)) && (emptyCount > 0)">
            <b-card
              v-for="empty in emptyCount"
              :key="`empty-${empty}`"
              class="empty"
            />
          </template>
        </b-card-group>

        <b-modal
          v-model="showRoomEditModal"
          size="lg"
          :title="$t('modal.conference_room.title')"
          :ok-title="$t('modal.conference_room.ok')"
          :cancel-title="$t('modal.conference_room.cancel')"
          cancel-variant="primary"
          :ok-disabled="editableRoom && (editableRoom.title.length === 0 || editableRoom.schedule.length === 0)"
          @ok="saveConferenceRoom()"
          no-close-on-backdrop
        >
          <conference-room-form
            :room="editableRoom"
            @apply="editableRoom = $event"
          />
        </b-modal>
      </b-col>
      <b-col
        sm="12"
        md="4"
        lg="3"
      >
        <b-card body-class="px-0 py-1">
          <sub-menu-block />
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import SubMenuBlock from '@/blocks/CabinetSubMenu'
import ConferenceRoomForm from '@/components/forms/ConferenceRoom'
import api from '@/api/conference'

export default {
  components: {
    SubMenuBlock,
    ConferenceRoomForm
  },
  data: () => ({
    showRoomEditModal: false,
    editableRoom: null,
    columns: 2
  }),
  computed: {
    userID () {
      return this.$store.getters['UserModule/userID']
    },
    currentParish () {
      return this.$store.getters['ParishModule/currentParish']
    },
    rooms () {
      let rooms = Object.values(this.$store.getters['ConferenceModule/rooms'])
        .sort((a, b) => a.title.localeCompare(b.title, 'ru'))
      if (this.isOwner) {
        rooms.push({ id: 'create' })
      }
      return rooms
    },
    noRooms () {
      return Object.values(this.$store.getters['ConferenceModule/rooms']).length === 0
    },
    isOwner () {
      return this.userID === this.currentParish.priest.id
    },
    breadcrumbs () {
      let items = []

      items.push({
        text: this.$t('cabinet.dashboard'),
        to: { name: 'cabinet' }
      })

      items.push({
        text: this.$t(`cabinet.blocks.conference.${this.currentParish.titles.conference}`),
        active: true
      })

      return items
    },
    rows () {
      let rows = []
      let i, j
      for (i = 0, j = this.rooms.length; i < j; i += this.columns) {
        rows.push(this.rooms.slice(i, i + this.columns))
      }
      return rows
    },
    emptyCount () {
      const rowsCount = Math.ceil(this.rooms.length / this.columns)
      return rowsCount * this.columns - this.rooms.length
    }
  },
  watch: {
    currentParish () {
      this.loadAvailableRooms()
    }
  },
  async mounted () {
    this.$busy()
    try {
      await this.loadAvailableRooms()
    } catch (e) {}
    this.$idle()
  },
  methods: {
    schedule (items) {
      let result = []

      const weekdays = {
        1: this.$t('dates.weekdays.monday'),
        2: this.$t('dates.weekdays.tuesday'),
        3: this.$t('dates.weekdays.wednesday'),
        4: this.$t('dates.weekdays.thursday'),
        5: this.$t('dates.weekdays.friday'),
        6: this.$t('dates.weekdays.saturday'),
        7: this.$t('dates.weekdays.sunday')
      }

      Object.values(items).forEach((item) => {
        result.push(this.$t('dates.time_in_day', { day: weekdays[item.weekday], time: item.time }))
      })

      return result
    },
    async loadAvailableRooms () {
      this.$busy()
      if (!this.currentParish) {
        await this.$store.dispatch('ParishModule/loadParishesList')
      }
      await this.$store.dispatch('ConferenceModule/loadRoomsList', this.currentParish)
      this.$idle()
    },
    selectConferenceRoom (room) {
      if (room.id === 'create') {
        this.editableRoom = { id: '', title: '', description: '', schedule: [], welcome: '', image: null }
        this.showRoomEditModal = true
      } else {
        this.join(room)
      }
    },
    async join (room) {
      try {
        const { url } = await api.getJoinURL(room)
        const win = window.open(url, '_blank')
        if (!win) {
          this.$bvToast.toast(`Похоже у Вас настроена блокировка всплывающих окон. Для входа в конференц-зал, нажмите на это сообщение.`, {
            variant: 'danger',
            title: 'Внимание!',
            solid: true,
            href: url,
            toaster: 'b-toaster-top-center',
            noAutoHide: true,
            noCloseButton: false
          })
        }
      } catch (error) {
        console.error(error.message)
        this.$bvToast.toast(error.message, {
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-center',
          noCloseButton: true
        })
      }
    },
    editConferenceRoom (room) {
      this.editableRoom = Object.assign({}, room)
      this.showRoomEditModal = true
    },
    async saveConferenceRoom () {
      this.$emit('loading')
      try {
        let actionName = 'ConferenceModule/updateRoom'
        let data = {
          room: this.editableRoom
        }
        if (!this.editableRoom.id) {
          actionName = 'ConferenceModule/openRoom'
          data['parish'] = this.currentParish
        }
        const { message } = await this.$store.dispatch(actionName, data)
        this.$bvToast.toast(message, {
          variant: 'info',
          solid: true,
          toaster: 'b-toaster-top-center',
          noCloseButton: true
        })
      } catch (error) {
        console.log(error.message)
        this.$bvToast.toast(error.message, {
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-center',
          noCloseButton: true
        })
      }
      this.$emit('loaded')
    },
    closeConferenceRoom (room) {
      this.$bvModal.msgBoxConfirm('Вы действительно хотите закрыть конференц-зал "' + room.title + '"?', {
        title: 'Внимание!',
        buttonSize: 'sm',
        okTitle: 'Да',
        cancelTitle: 'Нет'
      })
        .then(async confirmed => {
          if (confirmed) {
            const { message } = await this.$store.dispatch('ConferenceModule/closeRoom', { room })
            this.$bvToast.toast(message, {
              variant: 'info',
              solid: true,
              toaster: 'b-toaster-top-center',
              noCloseButton: true
            })
          }
        })
        .catch((error) => {
          console.log(error.message)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.card.empty,
.card.add-room {
  border-color: transparent;
  background-color: transparent;
}
.card.add-room {
  font-size: 3rem;
  color: rgb(113, 98, 111) !important;

  .card-body {
    background-color: rgba(180,180,180,.4);
    opacity: .5;
    &:hover {
      opacity: .8;
    }
  }
}

.room {
  .card-body:hover {
    opacity: .9;
  }

  &:hover {
    .hover {
      display: block;
    }
  }

  .hover {
    position: absolute;
    top: 3px;
    right: 3px;
    display: none;
    z-index: 100;
    color: rgb(255,255,255);

    button {
      opacity: .8;
    }

    button:hover {
      opacity: 1;
      // background-color: rgba(200,200,200, .5);
    }
  }
}
</style>
